import {
  Icon,
  Link,
  MenuBar,
  SkipLink,
  Tooltip,
  TopBar,
  TopBarItem
} from '@deque/cauldron-react';
import {
  AxeAccountProvider,
  NavBarAccountMenu,
  NavBarProductSwitcher,
  type ProductSwitcherItem
} from '@deque/ui-navbar';
import '@deque/ui-navbar/dist/styles/index.css';
import React, { type ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../contexts/auth';
import styles from './Header.css';

interface HeaderProps {
  navBar?: React.ReactNode;
  currentProduct: ProductSwitcherItem;
}

const Header = ({ navBar, currentProduct }: HeaderProps): ReactElement => {
  const { login, user } = useAuthContext();
  const { t } = useTranslation();
  const topMenuItemSupport = useRef<HTMLLIElement>(null);

  const handleLogin = (): void => {
    login();
  };

  const supportUrl = window.appConfig.SUPPORT_URL || '';

  return (
    <>
      <AxeAccountProvider
        authToken={user?.token || ''}
        accountPortalUrl={window.location.origin}
        productSlug={'axe-account'}
        sendUserActivity={false}
      >
        <header className={styles.header}>
          <SkipLink target="#main-content" aria-label={t('Skip')} />
          <TopBar>
            <MenuBar>
              <NavBarProductSwitcher currentProduct={currentProduct} />
              <TopBarItem
                className={`MenuItem--align-right MenuItem--separator ${styles.needHelp}`}
                menuItemRef={menuItemRef => {
                  if (menuItemRef) {
                    topMenuItemSupport.current = menuItemRef;
                  }
                }}
              >
                <Link
                  href={supportUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  aria-label={t('Need Help?')}
                  tabIndex={-1}
                >
                  <Icon
                    type="question-circle-alt"
                    label={t('opens support link')}
                    className={styles.needHelpIcon}
                  />
                  <span className={styles.needHelpText}>{t('Need Help?')}</span>
                </Link>
              </TopBarItem>
              <NavBarAccountMenu
                onLogin={handleLogin}
                onLogout={() => {
                  user?.logout({
                    redirectUri:
                      window.location.href.split('?socialProvider')[0]
                  });
                }}
                needHelpUrl={supportUrl}
              />
            </MenuBar>
          </TopBar>
          {topMenuItemSupport.current && (
            <Tooltip
              variant="info"
              target={topMenuItemSupport.current}
              placement="bottom"
              className={styles.supportTooltip}
            >
              {t('Click to open support page in a new tab.')}
            </Tooltip>
          )}
          {navBar || null}
        </header>
      </AxeAccountProvider>
    </>
  );
};

export default Header;
